import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { AiSuggestionsRightRailEntryPointValue } from '@atlaskit/editor-common/analytics/ai-suggestions-entry-points';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics/types/enums';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/useSharedPluginStateSelector';

import { scrollEditorHeadingIntoView } from '@confluence/editor-features/entry-points/scrollEditorHeadingIntoView.ts';
import { ObjectSidebarControl } from '@confluence/object-sidebar-components/entry-points/ObjectSidebarControl.tsx';
import { expValEquals } from '@confluence/feature-experiments';
import type { FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';
import { useShowSuggestionsPanel } from '@confluence/suggestions-panel/entry-points/useShowSuggestionsPanel.ts';
import { useUnseenSuggestions } from '@confluence/suggestions-panel/entry-points/useUnseenSuggestions.ts';

import { useSharedAIState } from './hooks/useSharedAIState.ts';

interface ObjectSidebarControlFullPageEditorProps {
	editorApi: FullPageEditorPresetAPI | undefined;
	hasAiButton: boolean;
	hasAudioButton: boolean;
	hasReviewButton?: boolean;
	contentId: string;
	spaceKey: string;
	contentType: string;
	contentTitle: string;
	isUnpublishedDraft?: boolean;
	disabled?: boolean;
	isEditorEmpty?: boolean;
	isMarkdownMode?: boolean;
	isMarkdownModeSyntaxViewToggled?: boolean;
}

export function ObjectSidebarControlFullPageEditor({
	editorApi,
	hasAiButton,
	hasAudioButton,
	hasReviewButton,
	contentId,
	spaceKey,
	contentType,
	contentTitle,
	isUnpublishedDraft,
	disabled,
	isEditorEmpty,
	isMarkdownMode,
	isMarkdownModeSyntaxViewToggled,
}: ObjectSidebarControlFullPageEditorProps): React.JSX.Element {
	const { aiIsStreaming } = useSharedAIState(editorApi);
	const fireAnalyticsEvent = editorApi?.analytics?.actions.fireAnalyticsEvent;

	const hasAiSuggestions = !!editorApi?.aiSuggestions;

	const thinkingStatus = useSharedPluginStateSelector(editorApi, 'aiSuggestions.thinkingStatus', {
		disabled: !hasAiSuggestions,
	});

	const suggestionById = useSharedPluginStateSelector(editorApi, 'aiSuggestions.suggestionById', {
		disabled: !hasAiSuggestions,
	});

	const selectedSuggestion = useSharedPluginStateSelector(
		editorApi,
		'aiSuggestions.selectedSuggestion',
		{
			disabled: !hasAiSuggestions,
		},
	);

	const latestSuggestionCreatedAt = useMemo(() => {
		if (!(suggestionById instanceof Map)) {
			return undefined;
		}
		let latest: string | undefined;
		suggestionById.forEach((suggestion) => {
			if (suggestion.createdAt && (!latest || suggestion.createdAt > latest)) {
				latest = suggestion.createdAt;
			}
		});
		return latest;
	}, [suggestionById]);

	const { isSuggestionsPanelShown, showSuggestionsPanel } = useShowSuggestionsPanel();
	const wasSuggestionsPanelShownRef = useRef(false);

	useEffect(() => {
		if (wasSuggestionsPanelShownRef.current && !isSuggestionsPanelShown) {
			editorApi?.aiSuggestions?.actions.selectSuggestion(null);

			fireAnalyticsEvent?.({
				action: ACTION.RIGHT_RAIL_CLOSED,
				actionSubject: ACTION_SUBJECT.AI_SUGGESTIONS,
				eventType: EVENT_TYPE.TRACK,
			});
		}
		wasSuggestionsPanelShownRef.current = isSuggestionsPanelShown;
	}, [editorApi, fireAnalyticsEvent, isSuggestionsPanelShown]);

	const { hasUnseenSuggestions, markSuggestionsSeen } = useUnseenSuggestions(
		contentId,
		latestSuggestionCreatedAt,
	);

	useEffect(() => {
		editorApi?.aiSuggestions?.actions.setUIVisibility(isSuggestionsPanelShown);
	}, [editorApi, isSuggestionsPanelShown]);

	// The completed toolbar is only visually hidden while this panel is open. Acknowledge the
	// completion here so it cannot reappear after the user has already viewed the suggestions.
	useEffect(() => {
		if (isSuggestionsPanelShown && thinkingStatus === 'complete') {
			editorApi?.aiSuggestions?.actions.setThinkingState('idle');
		}
	}, [editorApi, isSuggestionsPanelShown, thinkingStatus]);

	// Opening the panel, or selecting a suggestion card while it's closed, counts
	// as viewing the suggestions, so mark them seen and clear the dot. Gated on
	// the experiment and only written while there is something unseen, so it is
	// not an ungated side effect and does not re-fire once cleared.
	useEffect(() => {
		if (
			hasUnseenSuggestions &&
			(isSuggestionsPanelShown || selectedSuggestion) &&
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true)
		) {
			markSuggestionsSeen();
		}
	}, [hasUnseenSuggestions, isSuggestionsPanelShown, selectedSuggestion, markSuggestionsSeen]);

	const onReviewButtonClick = useCallback(() => {
		showSuggestionsPanel({ entryPoint: AiSuggestionsRightRailEntryPointValue.SUGGESTIONS_PANEL });
	}, [showSuggestionsPanel]);
	const aiSuggestionsButtonActions = useMemo(
		() =>
			hasAiSuggestions &&
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true)
				? {
						onStartThinking: onReviewButtonClick,
						fireAnalyticsEvent,
						isThinking: thinkingStatus === 'thinking',
						showNotificationDot: hasUnseenSuggestions,
					}
				: undefined,
		[
			fireAnalyticsEvent,
			hasAiSuggestions,
			onReviewButtonClick,
			thinkingStatus,
			hasUnseenSuggestions,
		],
	);

	const getAdfDocument = useCallback((): Promise<DocNode | null> => {
		return new Promise((resolve) => {
			if (editorApi?.core?.actions?.requestDocument) {
				editorApi.core.actions.requestDocument((doc) => {
					resolve(doc as DocNode);
				});
			} else {
				resolve(null);
			}
		});
	}, [editorApi]);

	return (
		<ObjectSidebarControl
			key="object-sidebar-control"
			hasCommentsButton
			hasDetailsButton
			hasLoomButton
			hasAiButton={hasAiButton}
			hasRemixButton
			hasReviewButton={hasReviewButton}
			hasAudioButton={hasAudioButton}
			aiSuggestionsButtonActions={aiSuggestionsButtonActions}
			aiIsStreaming={aiIsStreaming}
			contentId={contentId}
			spaceKey={spaceKey}
			contentType={contentType}
			contentTitle={contentTitle}
			isUnpublishedDraft={isUnpublishedDraft}
			disabled={disabled}
			isEditorEmpty={isEditorEmpty}
			isMarkdownMode={isMarkdownMode}
			isMarkdownModeSyntaxViewToggled={isMarkdownModeSyntaxViewToggled}
			getAdfDocument={getAdfDocument}
			editorApi={editorApi}
			scrollToEditorHeading={scrollEditorHeadingIntoView}
		/>
	);
}
